import React, { useState, useContext } from 'react';
import * as routes from '../constants/routes';
import { Alert, Button, Form, Row, Col } from 'react-bootstrap'
import { SvgTrashcanPrimaryBrand, SvgCalendar } from '../assets/icons';
import moment from 'moment';
import Loading from './Loading';
import MixpanelContext from 'react-mixpanel';

const DATE_FORMAT = 'YYYY-MM-DD'
const TIME_FORMAT = 'hh:mm'

function EditEntryTile({ entry, onDelete, onCancel, afterSubmit }) {
  const mixpanel = useContext(MixpanelContext)

  const { schedule } = entry;
  const isNew = !entry.id;

  const [deleting, setDeleting] = useState(false)
  const [validated, setValidated] = useState(false);
  const [titleValidated, setTitleValidated] = useState(true);
  const [timesValidated, setTimesValidated] = useState(true);

  const now = moment();
  let nextInterval = now;
  while(nextInterval.minutes() % 30 !== 0) {
    nextInterval.add(1, 'minutes')
  }
  let nextEndInterval = moment(nextInterval).add(30, 'minutes')
  const startAtMoment = !isNew ? moment(entry.startAt) : null;
  const endAtMoment = !isNew ? moment(entry.endAt) : null;

  const [title, setTitle] = useState(entry.title || '')
  const [notes, setNotes] = useState(entry.notes || '')
  const [startDate, setStartDate] = useState((startAtMoment || nextInterval).format(DATE_FORMAT))
  const [startTime, setStartTime] = useState((startAtMoment || nextInterval).format(TIME_FORMAT))
  const [endDate, setEndDate] = useState((endAtMoment || nextEndInterval).format(DATE_FORMAT))
  const [endTime, setEndTime] = useState((endAtMoment || nextEndInterval).format(TIME_FORMAT))
  const [submitting, setSubmitting] = useState(false)

  const startMomentState = moment(`${startDate} ${startTime}`)
  const endMomentState = moment(`${endDate} ${endTime}`)
  const valid = title.length > 1 && endMomentState > startMomentState;

  const validateTitle = () => {
    setTitleValidated(title.length > 0)
  }

  const validateTimes = () => {
    setTimesValidated(moment(`${endDate} ${endTime}`) - moment(`${startDate} ${startTime}`) > 0);
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setValidated(false);  
    }

    setValidated(true);

    const result = {
      id: entry.id,
      title,
      notes,
      startAt: moment(`${startDate} ${startTime}`).utc().format(),
      endAt: moment(`${endDate} ${endTime}`).utc().format(),
      scheduleId: schedule.id,
    }

    try {
      setSubmitting(true)
      await afterSubmit(result)
      if(isNew) {
        mixpanel.track('WFH_Add_Item_Complete')
      }
    } catch(e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    try {
      setSubmitting(true)
      await onDelete(entry)
      mixpanel.track('WFH_Delete_Item')
    } catch(e) {
      console.error(e)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Row>
      <Col xs={12}>
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <div className="editEntry tile" style={{marginTop: 28, position: 'relative'}}>
            <span className="title">{ isNew ? "Add" : "Edit" } Schedule Entry</span>
            { !isNew && <SvgTrashcanPrimaryBrand onClick={() => setDeleting(true)} style={{ cursor: 'pointer', float: 'right'}} />}
            <span className="subtitle">{schedule.title}</span>
            { deleting && <div style={{position: 'absolute', width: '100%', height: 108, backgroundColor: '#E4A300', marginLeft: -21, zIndex: 1000, padding: 18}}>
              <div style={{color: '#fff', fontSize: 14, fontWeight: 'bold', textAlign: 'center'}}>
                Are you sure you want to delete this event?
              </div>
              { submitting && <Loading style={{marginBottom: 40}} color="white" />}
              { !submitting && <>
                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'center', paddingTop: 18}}>
                  <button type="button" className="btn btn-warning" style={{marginRight: 5}} onClick={() => setDeleting(false)}>Nevermind</button>
                  <button type="button" className="btn btn-warning" onClick={handleDelete}>Yes, delete</button>
                </div>
              </>}
            </div>}
            <Form.Group>
              <Form.Control isInvalid={!titleValidated} type="text" required type="text" value={title} onBlur={validateTitle} onChange={(e) => { setTitle(e.target.value)}} className="form-control" placeholder="Team Meeting" />
              <Form.Control.Feedback type="invalid">
                A title is required
              </Form.Control.Feedback>
            </Form.Group>
            
            <input type="textarea" value={notes} onChange={(e) => setNotes(e.target.value)} className="form-control" placeholder="Video call, remember to wear pants" />

            <Form.Group>
              <div style={{marginTop: 14}}>
                <label>Start</label>
                <div style={{position: 'relative', display: 'flex', flexDirection: 'row'}}>
                  <SvgCalendar style={{position: 'absolute', top: 16, left: 12 }}/>
                  <Form.Control type="date"
                    className="form-control"
                    style={{ marginRight: 10 }}
                    value={startDate}
                    isInvalid={!timesValidated}
                    onBlur={validateTimes}
                    onChange={(e) => setStartDate(e.target.value)}
                    min={(startAtMoment || nextInterval).format(DATE_FORMAT)} />
                       <Form.Control type="time"
                    className="form-control"
                    step={60 * 15}
                    value={startTime}
                    isInvalid={!timesValidated}
                    onBlur={validateTimes}
                    onChange={(e) => setStartTime(e.target.value)}
                    min={(startAtMoment || nextInterval).format(TIME_FORMAT)} />
                </div>
              </div>
              <Form.Control.Feedback type="invalid">
                Start time must happen before the end time.
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group>
              <label>End</label>
              <div style={{marginTop: 4, marginBottom: 20}}>
                <div style={{position: 'relative', display: 'flex', flexDirection: 'row'}}>
                  <SvgCalendar style={{position: 'absolute', top: 16, left: 12 }}/>
                  <Form.Control type="date"
                    className="form-control"
                    style={{ marginRight: 10 }}
                    value={endDate}
                    isInvalid={!timesValidated}
                    onBlur={validateTimes}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={(endAtMoment || nextEndInterval).format(DATE_FORMAT)} />
                  <Form.Control type="time"
                    className="form-control"
                    step={60 * 15}
                    value={endTime}
                    isInvalid={!timesValidated}
                    onBlur={validateTimes}
                    onChange={(e) => setEndTime(e.target.value)}
                    min={(endAtMoment || nextEndInterval).format(TIME_FORMAT)} />
                </div>
              </div>
              <Form.Control.Feedback type="invalid">
                Start time must happen before the end time.
              </Form.Control.Feedback>
            </Form.Group>

            { !timesValidated && <Alert variant={'warning'}>The event can not be set to start later then it is scheduled to end.</Alert> }
          </div>
          <div style={{marginTop: 19, display: 'flex', flexDirection: 'row', justifyContent: 'flex-end'}}>
            { submitting && <Loading submitButtons={true} />}
            { !submitting && <>
              <button
                type="button"
                className="btn btn-none"
                onClick={() => { mixpanel.track('WFH_Add_Item_Cancel'); onCancel() }}
                style={{paddingLeft: 45, paddingRight: 45}}>
                  CANCEL
                </button>
              <Button 
                type="submit"
                className="btn btn-primary"
                disabled={!valid}
                style={{paddingLeft: 45, paddingRight: 45, marginRight: 30}}>
                SAVE
              </Button>
            </>}
          </div>
        </Form>
      </Col>
    </Row>
  );
}

export default EditEntryTile;
